import { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import Layout from '../../components/Layout';
import ItemTable from '../../components/ItemTable';
import {
  BodyText,
  Button,
  Checkbox,
  Input,
  Subtitle,
  Title,
} from '../../components/ui';
import CheckoutProgressbar from '../../components/CheckoutProgressbar';
import TermsModal from '../../components/TermsModal';
import { useNavigate } from 'react-router-dom';
import { FaRegTrashAlt, FaArrowLeft } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCartList,
  getCartShippingMethod,
  removeCart,
  updateCart,
} from '../../api/services/cartService';
import { getCustomerProfile } from '../../api/services/customerService';
import { getProductDetail } from '../../api/services/productService';
import { setUserCartList } from '../../redux/userCart';
import { setShowLoading } from '../../redux/systemInfo';
import { getOrderCost } from '../../api/services/utilService';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  height: auto;
  margin: 0;
  padding: 50px 0;
  align-items: center;

  @media (max-width: 768px) {
    padding: 30px 0;
  }

  @media (max-width: 480px) {
    padding: 20px 0;
  }
`;

const PageTitle = styled(Title)`
  width: 100%;
  text-align: center;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 0.75rem;
  }
`;

const Row = styled.div`
  display: flex;
  width: 100%;
  max-width: 100%;
  gap: 10px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    gap: 8px;
  }

  @media (max-width: 480px) {
    gap: 5px;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  gap: 10px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    gap: 8px;
  }

  @media (max-width: 480px) {
    gap: 5px;
  }
`;

const MainContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: 1450px;
  gap: 0;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 200px;
  padding: 2rem 1rem;

  @media (max-width: 768px) {
    flex-direction: row;
    width: 100%;
    min-width: auto;
    padding: 1rem;
    order: 1;
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (max-width: 768px) {
    order: 2;
  }
`;

const InnerContainer = styled.div`
  display: flex;
  width: 100%;
  gap: 1.5rem;
  margin-top: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 2.5;
  gap: 1rem;
  max-width: 1600px;
  min-width: 0;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
    max-width: none;
    overflow: visible;
    order: 2;
  }

  @media (max-width: 480px) {
    gap: 0.75rem;
  }
`;

const SideSection = styled.div`
  display: flex;
  flex: 0.6;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;

  @media (max-width: 768px) {
    width: 100%;
    order: 3;
  }

  @media (max-width: 480px) {
    gap: 0.75rem;
  }
`;

const VoucherBox = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  gap: 1rem;
  box-sizing: border-box;

  @media (max-width: 768px) {
    gap: 0.75rem;
  }
`;

const VoucherContainer = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const VoucherHeader = styled.div`
  width: 100%;
  padding: 30px;
  border-radius: ${(props) => (props.isOpen ? '10px 10px 0 0' : '10px')};
  background: ${(props) => props.theme.colors.subBackground};
  cursor: pointer;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 20px;
  }

  @media (max-width: 480px) {
    padding: 15px;
  }
`;

const VoucherDetail = styled.div`
  width: 100%;
  border-radius: 0 0 10px 10px;
  display: flex;
  flex-direction: column;
  padding: 20px;
  border: 1px solid ${(props) => props.theme.colors.subBackground};
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 15px;
  }

  @media (max-width: 480px) {
    padding: 10px;
  }
`;

const ClickableText = styled.span`
  color: ${(props) => props.theme.colors.primaryBlue};
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const InputContainer = styled.div`
  width: 80%;
  max-width: 100%;
  border: 1px solid #ccc;
  margin-bottom: 20px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: 15px;
  }

  @media (max-width: 480px) {
    margin-bottom: 10px;
  }
`;

const CommentBox = styled.div`
  width: 100%;
  max-width: 100%;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  background: ${(props) => props.theme.colors.subBackground};
  padding: 30px;
  margin: 2px 0;
  gap: 10px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 20px;
    gap: 8px;
  }

  @media (max-width: 480px) {
    padding: 15px;
    gap: 5px;
  }
`;

const CommentInput = styled.textarea`
  box-sizing: border-box;
  font-family: 'Roboto', sans-serif;
  min-width: 100%;
  max-width: 100%;
  min-height: 150px;
  max-height: 300px;
  padding: 10px;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 10px;
  /* resize: vertical; */
  overflow: auto;
  &:focus {
    outline: none;
  }

  @media (max-width: 768px) {
    min-height: 120px;
    padding: 8px;
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    min-height: 100px;
    padding: 6px;
    font-size: 0.85rem;
  }
`;

const TotalBox = styled.div`
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 30px;
  border-radius: 10px;
  background: ${(props) => props.theme.colors.subBackground};
  padding: 10% 20px;
  font-size: 18px;
  font-weight: bold;
  text-align: right;
  box-sizing: border-box;

  @media (max-width: 768px) {
    gap: 20px;
    padding: 20px;
    font-size: 16px;
  }

  @media (max-width: 480px) {
    gap: 15px;
    padding: 15px;
    font-size: 14px;
  }
`;

const CartPage = () => {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.userInfo);
  const userCart = useSelector((state) => state.userCart);

  const theme = useTheme();
  const navigate = useNavigate();

  const [code, setCode] = useState('');
  const [comment, setComment] = useState('');

  const [isAgree, setIsAgree] = useState(false);
  const [isVoucherOpen, setIsVoucherOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isGuestUser, setIsGuestUser] = useState(false);

  const [totalItemPrice, setTotalItemPrice] = useState(0);
  const [totalTaxPrice, setTotalTaxPrice] = useState(0);
  const [totalShippingPrice, setTotalShippingPrice] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [shippingMethodName, setShippingMethodName] = useState('');

  const calculateCartSubtotal = () =>
    userCart.cart_list.reduce((sum, item) => sum + item.price * item.totQty, 0);

  useEffect(() => {
    getDbCost();
  }, [userCart.cart_list]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const checkUserRole = async () => {
      try {
        const profileResult = await getCustomerProfile();
        if (profileResult?.data?.role === 'Guest') {
          setIsGuestUser(true);
        } else {
          setIsGuestUser(false);
        }
      } catch (error) {
        console.error('Error checking user role:', error);
      }
    };

    if (userInfo.idCustomer) {
      checkUserRole();
    }
  }, [userInfo.idCustomer]);

  const getDbCost = async () => {
    dispatch(setShowLoading({ showLoading: true }));
    const fallbackSubtotal = calculateCartSubtotal();

    try {
      const result = await getOrderCost(userInfo.idCustomer, {
        sessionID: userInfo.idCustomer,
        couponCd: code,
        point: 0,
        shipFee: 0,
        otherFee: 0,
        zipcode: userInfo.zipcode,
        idStore: userInfo.idStore,
      });
      const { output_subTotAmt, output_taxAmt, output_totAmt } =
        result.orderAmount;

      const hasValidSubtotal =
        Number(output_subTotAmt) > 0 || fallbackSubtotal === 0;

      if (hasValidSubtotal) {
        setTotalItemPrice(output_subTotAmt);
        setTotalTaxPrice(output_taxAmt);
        setTotalPrice(output_totAmt);
        try {
          const result = await getOrderCost(userInfo.idCustomer, {
            sessionID: userInfo.idCustomer,
            couponCd: code,
            point: 0,
            shipFee: 0,
            otherFee: 0,
            zipcode: userInfo.zipcode,
            idStore: userInfo.idStore,
          });
          const { output_subTotAmt, output_taxAmt, output_totAmt } =
            result.orderAmount;

          const hasValidSubtotal =
            Number(output_subTotAmt) > 0 || fallbackSubtotal === 0;

          if (hasValidSubtotal) {
            setTotalItemPrice(output_subTotAmt);
            setTotalTaxPrice(output_taxAmt);
            setTotalPrice(output_totAmt);
          } else {
            setTotalItemPrice(fallbackSubtotal);
            setTotalTaxPrice(0);
            setTotalPrice(fallbackSubtotal);
          }
        } catch (error) {
          console.error('Error fetching cart cost:', error);
          setTotalItemPrice(fallbackSubtotal);
          setTotalTaxPrice(0);
          setTotalPrice(fallbackSubtotal);
        } finally {
          dispatch(setShowLoading({ showLoading: false }));
        }
      } else {
        setTotalItemPrice(fallbackSubtotal);
        setTotalTaxPrice(0);
        setTotalPrice(fallbackSubtotal);
      }
    } catch (error) {
      console.error('Error fetching cart cost:', error);
      setTotalItemPrice(fallbackSubtotal);
      setTotalTaxPrice(0);
      setTotalPrice(fallbackSubtotal);
    } finally {
      dispatch(setShowLoading({ showLoading: false }));
    }
  };

  const getDbCartList = async () => {
    dispatch(setShowLoading({ showLoading: true }));
    if (userInfo.zipcode) {
      const result = await getCartList(
        userInfo.idCustomer,
        userInfo.zipcode,
        userInfo.idStore,
      );

      // Enrich cart items with avaQty from product details
      const enrichedCartList = await Promise.all(
        result.cartList.map(async (item) => {
          // If avaQty is already present, use it; otherwise fetch from product detail
          if (item.avaQty !== undefined && item.avaQty !== null) {
            return item;
          }

          try {
            const productResult = await getProductDetail(
              item.idStyle,
              userInfo.idCustomer,
              userInfo.idStore,
              '01', // Always use price level 01
            );
            return {
              ...item,
              avaQty: productResult.product?.avaQty || 0,
            };
          } catch (error) {
            console.error(
              `Error fetching product detail for ${item.idStyle}:`,
              error,
            );
            return item; // Return item without avaQty if fetch fails
          }
        }),
      );

      const totPrice = enrichedCartList.reduce(
        (sum, item) => sum + item.price * item.totQty,
        0,
      );
      setTotalPrice(totPrice);
      dispatch(setUserCartList({ cart_list: enrichedCartList }));
    }
    dispatch(setShowLoading({ showLoading: false }));
  };

  const fetchCustomerShippingMethod = async () => {
    try {
      const result = await getCartShippingMethod(userInfo.idCustomer);
      if (result.is_success && result.data && result.data.shipViaName) {
        setShippingMethodName(result.data.shipViaName);
      }
    } catch (error) {
      console.error('Error fetching cart shipping method:', error);
    }
  };

  useEffect(() => {
    if (userInfo.idCustomer) {
      fetchCustomerShippingMethod();
    }
  }, [userInfo.idCustomer]);

  const handleApplyCode = () => {
    getDbCost();
  };

  const handleCheckout = async () => {
    if (!isAgree) {
      alert('Please agree terms of service!');
      return;
    }

    if (isGuestUser) {
      return; // Button is disabled, but extra safety check
    }

    // Proceed to checkout
    navigate('/checkout', { state: { comment, code } });
  };

  const handleUpdateCart = async (item, quantity) => {
    const result = await updateCart(
      userInfo.idCustomer,
      userInfo.zipcode,
      item.idStyle,
      item.idStyleDtl,
      quantity,
    );
    if (result.is_success) {
      getDbCartList();
    } else {
      alert(result.message || 'Error while updating cart');
    }
  };

  const handleRemoveCart = async (item) => {
    const result = await removeCart(userInfo.idCustomer, item.idCartDtl);
    if (result.is_success) {
      getDbCartList();
      alert(`${item.webTitle} remove from cart!`);
    } else {
      alert(`Error while add to cart`);
    }
  };

  const handleClearCart = async () => {
    for (const cart of userCart.cart_list) {
      const result = await removeCart(userInfo.idCustomer, cart.idCartDtl);
      if (!result.is_success) {
        alert(`Error while add to cart`);
      }
    }
    getDbCartList();
  };

  return (
    <Layout>
      {userCart.cart_list.length > 0 ? (
        <Container>
          <PageTitle>My Cart</PageTitle>
          <MainContainer>
            <ProgressSection>
              <CheckoutProgressbar currentStep={0} horizontal={isMobile} />
            </ProgressSection>
            <ContentWrapper>
              <InnerContainer>
                <MainSection>
                  <ItemTable
                    style={{ width: '100%', maxWidth: '100%' }}
                    data={userCart.cart_list}
                    updateData={handleUpdateCart}
                    removeData={handleRemoveCart}
                  />
                  <Row
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      style={{ background: theme.colors.primaryBlue }}
                      onClick={() => navigate(-1)}
                    >
                      <FaArrowLeft style={{ marginRight: '10px' }} />
                      Continue Shopping
                    </Button>
                    <Button
                      style={{ background: '#6c757d' }}
                      onClick={handleClearCart}
                    >
                      <FaRegTrashAlt style={{ marginRight: '10px' }} />
                      Clear Shopping Cart
                    </Button>
                  </Row>
                </MainSection>

                <SideSection>
                  {shippingMethodName && (
                    <Row style={{ marginBottom: '1rem' }}>
                      <BodyText weight='bold'>Shipping Method: </BodyText>
                      <BodyText>{shippingMethodName}</BodyText>
                    </Row>
                  )}
                  <VoucherBox>
                    <VoucherContainer>
                      <VoucherHeader
                        isOpen={isVoucherOpen}
                        onClick={() => setIsVoucherOpen(!isVoucherOpen)}
                      >
                        <span>{isVoucherOpen ? '▲  ' : '▼  '}</span>
                        {`Discount codes`}
                      </VoucherHeader>
                      {isVoucherOpen ? (
                        <VoucherDetail>
                          <Subtitle color={theme.colors.primaryText}>
                            Discount Code
                          </Subtitle>
                          <BodyText>Enter your coupon here</BodyText>
                          <Row>
                            <InputContainer>
                              <Input
                                type='text'
                                value={code}
                                onChange={(value) => setCode(value)}
                              />
                            </InputContainer>
                            <Button onClick={handleApplyCode}>
                              <Subtitle color={theme.colors.white}>
                                Apply
                              </Subtitle>
                            </Button>
                          </Row>
                        </VoucherDetail>
                      ) : null}
                    </VoucherContainer>
                  </VoucherBox>
                  <TotalBox>
                    <Column>
                      <Row style={{ justifyContent: 'space-between' }}>
                        <Subtitle>Total:</Subtitle>
                        <Subtitle>{`$${totalItemPrice.toFixed(2)}`}</Subtitle>
                      </Row>
                      <Row style={{ justifyContent: 'space-between' }}>
                        <Subtitle>Shipping:</Subtitle>
                        <Subtitle>{`$${totalShippingPrice.toFixed(2)}`}</Subtitle>
                      </Row>
                      <Row style={{ justifyContent: 'space-between' }}>
                        <Subtitle>Tax:</Subtitle>
                        <Subtitle>{`$${totalTaxPrice.toFixed(2)}`}</Subtitle>
                      </Row>
                      <Row style={{ justifyContent: 'space-between' }}>
                        <Subtitle weight='bold' size='1.1rem'>
                          Total:
                        </Subtitle>
                        <Subtitle weight='bold' size='1.1rem'>
                          {`$${totalPrice.toFixed(2)}`}
                        </Subtitle>
                      </Row>
                    </Column>
                    <Row>
                      <Checkbox
                        checked={isAgree}
                        onChange={(e) => {
                          setIsAgree(e.target.checked);
                        }}
                      />
                      <BodyText style={{ maxWidth: '80%' }}>
                        {`I agree with the terms of service and I adhere to them unconditionally `}
                        <ClickableText
                          onClick={() => setIsTermsModalOpen(true)}
                        >
                          (read)
                        </ClickableText>
                      </BodyText>
                    </Row>
                    <Button
                      style={{ width: '100%' }}
                      onClick={handleCheckout}
                      disabled={isGuestUser}
                    >
                      <BodyText
                        color={theme.colors.white}
                        weight='bold'
                        size='0.9rem'
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        Proceed To Checkout
                      </BodyText>
                    </Button>
                    {isGuestUser && (
                      <BodyText
                        size='0.85rem'
                        color={theme.colors.primaryRed}
                        style={{ marginTop: '10px', textAlign: 'center' }}
                      >
                        Guest users do not have access to checkout. Please
                        contact us to upgrade your account.
                      </BodyText>
                    )}
                  </TotalBox>
                  <Subtitle
                    color={theme.colors.primaryRed}
                    style={{ textAlign: 'center' }}
                  >
                    Shipping fee will be added to invoice when order is ready to
                    be shipped.
                  </Subtitle>
                  <CommentBox>
                    <Subtitle color={theme.colors.primaryText}>
                      Comments
                    </Subtitle>
                    <CommentInput
                      value={comment}
                      onChange={(e) => {
                        setComment(e.target.value);
                      }}
                    />
                  </CommentBox>
                </SideSection>
              </InnerContainer>
            </ContentWrapper>
          </MainContainer>
        </Container>
      ) : (
        <Container>
          <PageTitle>My Cart</PageTitle>
          <Subtitle style={{ textAlign: 'center', margin: '3rem 0 10rem' }}>
            Your Shopping Cart is empty!
          </Subtitle>
        </Container>
      )}
      <TermsModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
      />
    </Layout>
  );
};

export default CartPage;
